import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { domainApi, type BrandingConfig } from '../api/domain'
import { SITE_NAME, SITE_TAGLINE } from '../seo/siteConfig'
import { defaultContent } from '../data/defaultContent'
import { mergeContent, type StorefrontContent } from '../data/content'

/**
 * Runtime, per-brand identity. Because the storefront is ONE shared build served
 * for many brand hosts (zenzers4life.com, crownring.zenzers4life.com, …), the
 * brand can only be known at runtime from `window.location.host`. This provider
 * fetches `/api/domain/config` (which the backend resolves by Host), applies the
 * theme as CSS custom properties, swaps the favicon, and exposes the brand name/
 * logo to components. It is SSG-safe: all work happens in a client effect and it
 * falls back to the built-in Zenzers defaults until (or unless) config loads.
 */

export interface Branding {
  siteName: string
  tagline: string
  logoUrl: string | null
  primary: string | null
  accent: string | null
  skin: string | null
  tenantId: string | null
  loading: boolean
}

const DEFAULT_BRANDING: Branding = {
  siteName: SITE_NAME,
  tagline: SITE_TAGLINE,
  logoUrl: null,
  primary: null,
  accent: null,
  skin: null,
  tenantId: null,
  loading: true,
}

const BrandingContext = createContext<Branding>(DEFAULT_BRANDING)
const ContentContext = createContext<StorefrontContent>(defaultContent)

export function useBranding(): Branding {
  return useContext(BrandingContext)
}

/** Per-brand marketing content (brand overrides deep-merged over defaults). */
export function useStorefrontContent(): StorefrontContent {
  return useContext(ContentContext)
}

// Inject (or clear) a custom template's CSS into a dedicated <style> element.
// Idempotent: reuses the same node so re-fetches don't stack styles.
function applySkinCss(css: string) {
  if (typeof document === 'undefined') return
  const ID = 'brand-skin-css'
  let el = document.getElementById(ID) as HTMLStyleElement | null
  if (!css) { if (el) el.textContent = ''; return }
  if (!el) {
    el = document.createElement('style')
    el.id = ID
    document.head.appendChild(el)
  }
  el.textContent = css
}

function applyTheme(b: BrandingConfig) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const primary = b.theme?.primary
  const accent = b.theme?.accent
  if (primary) root.style.setProperty('--brand-primary', primary)
  if (accent) root.style.setProperty('--brand-accent', accent)

  if (b.favicon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = b.favicon
  }
}

/**
 * Runtime per-domain SEO. SSG bakes the default (Zenzers) canonical/title/OG at
 * build time; on a brand host we rewrite them client-side so JS-rendering
 * crawlers (Google) see the brand's own canonical + name. Guarded so the default
 * brand is left untouched.
 */
function applySeo(siteName: string) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const host = window.location.host
  const origin = `${window.location.protocol}//${host}`
  const url = origin + window.location.pathname

  const setAttr = (selector: string, attr: string, val: string) => {
    const el = document.querySelector(selector)
    if (el) el.setAttribute(attr, val)
  }
  // Per-domain canonical + OG url
  setAttr('link[rel="canonical"]', 'href', url)
  setAttr('meta[property="og:url"]', 'content', url)

  // Brand-ize the baked default name in title/name meta (only when overridden)
  if (siteName && siteName !== SITE_NAME) {
    if (document.title.includes(SITE_NAME)) {
      document.title = document.title.split(SITE_NAME).join(siteName)
    }
    for (const sel of ['meta[name="description"]', 'meta[property="og:description"]',
      'meta[property="og:title"]', 'meta[name="twitter:title"]', 'meta[name="twitter:description"]']) {
      const el = document.querySelector(sel)
      const c = el?.getAttribute('content')
      if (el && c && c.includes(SITE_NAME)) el.setAttribute('content', c.split(SITE_NAME).join(siteName))
    }
    setAttr('meta[property="og:site_name"]', 'content', siteName)
  }
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING)
  const [content, setContent] = useState<StorefrontContent>(defaultContent)

  useEffect(() => {
    let cancelled = false
    domainApi
      .getConfig()
      .then((cfg) => {
        if (cancelled) return
        const b = cfg?.branding
        if (b) applyTheme(b)
        applySeo(b?.siteName || SITE_NAME)
        // Toggle the per-brand visual skin on the root element. Built-in skins
        // (luxury/simple) ship their CSS in the bundle; custom templates arrive as
        // `skinCss` and are injected into a dedicated <style> element (applied via
        // [data-skin="<key>"]). Injecting only when non-empty keeps built-ins fast.
        if (typeof document !== 'undefined') {
          if (b?.skin) document.documentElement.dataset.skin = b.skin
          else delete document.documentElement.dataset.skin
          applySkinCss(cfg?.skinCss || '')
        }
        setBranding({
          siteName: b?.siteName || SITE_NAME,
          tagline: b?.tagline || SITE_TAGLINE,
          logoUrl: b?.logoUrl || null,
          primary: b?.theme?.primary || null,
          accent: b?.theme?.accent || null,
          skin: b?.skin || null,
          tenantId: cfg?.tenantId || null,
          loading: false,
        })
        // Deep-merge any per-brand content overrides over the Zenzers defaults.
        if (cfg?.content) setContent(mergeContent(cfg.content))
      })
      .catch(() => {
        // No custom domain config (or request failed) — keep Zenzers defaults.
        if (!cancelled) setBranding((prev) => ({ ...prev, loading: false }))
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <BrandingContext.Provider value={branding}>
      <ContentContext.Provider value={content}>{children}</ContentContext.Provider>
    </BrandingContext.Provider>
  )
}
